"use client";

import type { GetSoundboardResponse } from "@/app/_api/soundboards";
import type { User } from "firebase/auth";
import type { ReactNode } from "react";
import type React from "react";

import { createContext, useContext, useReducer } from "react";

import type { ApiPreset, MidiDevice, Preset, Sound, SoundMetadata, SoundSlot } from "../types";
import type { Action, PanelState } from "./actions";

import { DEFAULT_ICONS } from "../data";
import { PresetCategory } from "../types";
import { createSoundboardPlaceholder } from "../utils";
import { ActionTypes, MAX_SOUND_SLOTS, PanelType } from "./actions";

// Define the state structure
export interface State {
  activePresetCategory: PresetCategory;
  activePresetId: null | string;
  isInEditMode: boolean;
  isInLoopEditMode: boolean;
  isLoadingSavedPresets?: boolean;
  isPaused: boolean;
  maxConcurrentSounds: number;
  // MIDI state
  midi: {
    activeDeviceId: null | string;
    devices: MidiDevice[];
    enabled: boolean;
    error?: string;
    mapping?: Record<number, number>;
  };
  originalPresets: { [id: string]: Preset };
  panel: PanelState | null;
  playingSounds: { [id: string]: boolean };
  presets: { [id: string]: Preset };
  soundMetadata: Record<string, SoundMetadata>; // Metadata for each sound, keyed by URL
  user: User | null | undefined;
  volume: number;
}

export type SerializedState = Omit<State, "playingSounds" | "soundMetadata">;

// Initial state
const initialState: State = {
  activePresetCategory: PresetCategory.DEFAULTS,
  activePresetId: null,
  isPaused: false,
  maxConcurrentSounds: 6,
  panel: null,
  playingSounds: {},
  originalPresets: {},
  presets: {},
  soundMetadata: {},
  volume: 0.5,
  isInEditMode: false,
  isInLoopEditMode: false,
  user: undefined,
  isLoadingSavedPresets: false,
  // Initialize MIDI state
  midi: {
    enabled: false,
    devices: [],
    activeDeviceId: null,
  },
};

/**
 * Serializes the state by removing transient properties that don't need to be persisted.
 * This includes runtime-specific data like sound metadata and currently playing sounds.
 *
 * @param state - The complete state object
 * @returns A serialized version of the state suitable for storage
 */
export function serializeState(state: State): SerializedState {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { soundMetadata, playingSounds, ...persistentState } = state;
  return persistentState;
}

const initializePresetSlots = (preset: ApiPreset): Preset => {
  const sounds: SoundSlot[] = [...preset.sounds].map(sound => ({
    ...sound,
    type: "sound",
    id: sound.defaultSoundId,
    defaultSoundId: sound.defaultSoundId,
  }));

  while (sounds.length < MAX_SOUND_SLOTS) {
    sounds.push(createSoundboardPlaceholder(sounds.length));
  }
  return {
    id: preset.name,
    name: preset.name,
    icon: preset.icon,
    sounds,
    loaded: true,
    hasEdits: false,
    isPublic: false,
  };
};

const createPresetFromCustomSoundboard = (soundboard: GetSoundboardResponse, isFullyLoaded: boolean): Preset => {
  const presetSounds: SoundSlot[] = soundboard.sounds.map(sound => ({
    id: sound.sound_id,
    savedSoundId: sound.sound_id,
    name: sound.name,
    type: "sound",
    url: sound.url,
  }));

  while (presetSounds.length < MAX_SOUND_SLOTS) {
    presetSounds.push(createSoundboardPlaceholder(presetSounds.length));
  }

  return {
    id: soundboard.soundboard_id,
    name: soundboard.name,
    icon: DEFAULT_ICONS[soundboard.icon_id as keyof typeof DEFAULT_ICONS] ?? undefined,
    sounds: presetSounds,
    loaded: isFullyLoaded,
    savedBoardId: soundboard.soundboard_id,
    hasEdits: false,
    isPublic: soundboard.public,
  };
};

const hasPresetChanged = (preset: Preset, originalPreset: Preset | undefined): boolean => {
  return JSON.stringify(preset) !== JSON.stringify(originalPreset);
};

// Reducer function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.LOAD_PRESETS: {
      const presets = action.payload.map(initializePresetSlots);
      return {
        ...state,
        presets: presets.reduce((acc, preset) => ({ ...acc, [preset.id]: preset }), {}),
        activePresetId: presets[0]?.id ?? null,
        originalPresets: presets.reduce((acc, preset) => ({ ...acc, [preset.id]: preset }), {}),
      };
    }

    case ActionTypes.ACTIVATE_PRESET:
      return { ...state, activePresetId: action.payload };

    case ActionTypes.ADD_SOUND: {
      const { presetId, slotIndex, sound } = action.payload;
      const presetToUpdate = state.presets[presetId];
      if (!presetToUpdate) return state;
      const updatedPreset = {
        ...presetToUpdate,
        sounds: presetToUpdate.sounds.map((slot, index) => (index === slotIndex ? sound : slot)),
      };

      return {
        ...state,
        presets: {
          ...state.presets,
          [presetId]: {
            ...updatedPreset,
            hasEdits: hasPresetChanged(presetToUpdate, updatedPreset),
          },
        },
      };
    }
    case ActionTypes.UPDATE_SOUND_NAME: {
      const { presetId, slotIndex, soundName } = action.payload;
      const presetToUpdate = state.presets[presetId];
      if (!presetToUpdate) return state;
      const updatedPreset = {
        ...presetToUpdate,
        sounds: presetToUpdate.sounds.map((slot, index) => (index === slotIndex ? { ...slot, name: soundName } : slot)),
      };
      return {
        ...state,
        presets: {
          ...state.presets,
          [presetId]: {
            ...updatedPreset,
            hasEdits: hasPresetChanged(presetToUpdate, updatedPreset),
          },
        },
      };
    }

    case ActionTypes.ADD_PRESET: {
      const newPreset: Preset = {
        id: `preset-${Object.keys(state.presets).length + 1}`,
        name: "Custom Preset",
        icon: "",
        sounds: Array(MAX_SOUND_SLOTS)
          .fill(null)
          .map((_, index) => createSoundboardPlaceholder(index)),
        loaded: true,
        hasEdits: true,
        isPublic: false,
        isCreatedByMe: true,
      };

      return {
        ...state,
        presets: {
          ...state.presets,
          [newPreset.id]: newPreset,
        },
        activePresetId: newPreset.id,
        activePresetCategory: PresetCategory.OWN,
        isInEditMode: true,
        isInLoopEditMode: false,
        panel: {
          type: PanelType.PRESET_EDITOR,
          data: { presetId: newPreset.id },
        },
      };
    }

    case ActionTypes.UPDATE_PRESET: {
      const { id, name, icon } = action.payload;
      const presetToUpdate = state.presets[id];
      if (!presetToUpdate) return state;
      const updatedPreset = { ...presetToUpdate, name, icon };
      return {
        ...state,
        presets: {
          ...state.presets,
          [id]: { ...updatedPreset, hasEdits: hasPresetChanged(presetToUpdate, updatedPreset) },
        },
      };
    }

    case ActionTypes.OPEN_PANEL: {
      return {
        ...state,
        activePresetId: action.payload.data?.presetId ?? state.activePresetId,
        panel: action.payload,
      };
    }

    case ActionTypes.CLOSE_PANEL: {
      return {
        ...state,
        isInEditMode: false,
        panel: null,
      };
    }

    case ActionTypes.CLEAR_SOUND: {
      const { presetId, slotIndex } = action.payload;
      const presetToUpdate = state.presets[presetId];
      if (!presetToUpdate) return state;
      const updatedPreset = {
        ...presetToUpdate,
        sounds: presetToUpdate.sounds.map((slot, index) =>
          index === slotIndex ? createSoundboardPlaceholder(index) : slot,
        ),
      };
      return {
        ...state,
        presets: {
          ...state.presets,
          [presetId]: {
            ...updatedPreset,
            hasEdits: hasPresetChanged(presetToUpdate, updatedPreset),
          },
        },
      };
    }

    case ActionTypes.SOUND_STARTED: {
      const { soundId } = action.payload;
      // Remove from queue and add to playing sounds
      return {
        ...state,
        playingSounds: { ...state.playingSounds, [soundId]: true },
      };
    }

    case ActionTypes.SOUND_ENDED: {
      const { soundId } = action.payload;
      // Remove from playing sounds and clear current time
      const metadata = state.soundMetadata[soundId];
      const newPlayingSounds = { ...state.playingSounds };
      delete newPlayingSounds[soundId];
      return {
        ...state,
        playingSounds: newPlayingSounds,
        soundMetadata: {
          ...state.soundMetadata,
          [soundId]: { ...metadata, currentTime: undefined },
        },
      };
    }

    case ActionTypes.SOUND_LOADED: {
      const { soundId, duration } = action.payload;
      return {
        ...state,
        soundMetadata: {
          ...state.soundMetadata,
          [soundId]: {
            duration,
            isLoaded: true,
            currentTime: undefined,
          },
        },
      };
    }

    case ActionTypes.SOUND_PROGRESS: {
      const { soundId, currentTime } = action.payload;
      const metadata = state.soundMetadata[soundId];
      if (!metadata || !state.playingSounds[soundId]) return state;

      return {
        ...state,
        soundMetadata: {
          ...state.soundMetadata,
          [soundId]: { ...metadata, currentTime },
        },
      };
    }

    case ActionTypes.SET_VOLUME: {
      const { volume } = action.payload;
      return { ...state, volume };
    }

    case ActionTypes.TOGGLE_SOUND_LOOP: {
      const { soundId } = action.payload;
      return {
        ...state,
        presets: {
          ...state.presets,
          [state.activePresetId!]: {
            ...state.presets[state.activePresetId!],
            sounds: state.presets[state.activePresetId!].sounds.map(sound =>
              sound.id === soundId ? { ...sound, isLooped: !(sound as Sound).isLooped } : sound,
            ),
          },
        },
      };
    }

    case ActionTypes.TOGGLE_EDIT_MODE: {
      return { ...state, isInEditMode: !state.isInEditMode, isInLoopEditMode: false, panel: null };
    }

    case ActionTypes.TOGGLE_LOOP_EDIT_MODE: {
      return { ...state, isInLoopEditMode: !state.isInLoopEditMode, isInEditMode: false, panel: null };
    }

    case ActionTypes.TOGGLE_IS_PAUSED: {
      return { ...state, isPaused: !state.isPaused };
    }

    case ActionTypes.ADD_ROW: {
      const activePreset = state.presets[state.activePresetId!];
      const newPlacholders = Array(4)
        .fill(null)
        .map((_, index) => createSoundboardPlaceholder(activePreset?.sounds.length ?? 0 + index));
      return {
        ...state,
        presets: {
          ...state.presets,
          [state.activePresetId!]: { ...activePreset, sounds: [...activePreset.sounds, ...newPlacholders] },
        },
      };
    }
    case ActionTypes.RESET_PRESET: {
      const { presetId } = action.payload;
      if (!state.originalPresets[presetId]) return state;
      return {
        ...state,
        presets: {
          ...state.presets,
          [presetId]: state.originalPresets[presetId],
        },
      };
    }

    case ActionTypes.CREATE_SOUNDBOARD_SUCCESS: {
      const { soundboard, existingPresetId } = action.payload;
      const newPreset = { ...createPresetFromCustomSoundboard(soundboard, false), isCreatedByMe: true };

      const newPresets = { ...state.presets, [newPreset.id]: newPreset };
      if (existingPresetId) {
        delete newPresets[existingPresetId];
      }
      return {
        ...state,
        presets: newPresets,
        originalPresets: { ...state.originalPresets, [newPreset.id]: newPreset },
        activePresetCategory: PresetCategory.OWN,
        activePresetId: newPreset.id,
        panel: null,
      };
    }

    case ActionTypes.UPDATE_SOUNDBOARD_SUCCESS: {
      const { soundboard } = action.payload;
      const updatedPreset = { ...createPresetFromCustomSoundboard(soundboard, false), isCreatedByMe: true };

      return {
        ...state,
        presets: {
          ...state.presets,
          [updatedPreset.id]: { ...updatedPreset, hasEdits: false },
        },
        originalPresets: { ...state.originalPresets, [updatedPreset.id]: updatedPreset },
      };
    }

    case ActionTypes.LOAD_CUSTOM_SOUNDBOARD_SUCCESS: {
      const { soundboard } = action.payload;
      const preset = {
        ...createPresetFromCustomSoundboard(soundboard, true),
        isCreatedByMe: soundboard.created_by_user_id === state.user?.uid,
      };

      return {
        ...state,
        presets: { ...state.presets, [preset.id]: preset },
        originalPresets: { ...state.originalPresets, [preset.id]: preset },
        activePresetCategory: preset.isCreatedByMe ? PresetCategory.OWN : state.activePresetCategory,
        activePresetId: preset.id,
      };
    }

    case ActionTypes.LOAD_MY_SOUNDBOARDS_START: {
      return { ...state, isLoadingSavedPresets: true };
    }

    case ActionTypes.LOAD_MY_SOUNDBOARDS_SUCCESS: {
      const { soundboards } = action.payload;
      return {
        ...state,
        presets: {
          ...state.presets,
          ...soundboards.reduce(
            (acc, board) => ({
              ...acc,
              [board.soundboard_id]: { ...createPresetFromCustomSoundboard(board, false), isCreatedByMe: true },
            }),
            {},
          ),
        },
        originalPresets: {
          ...state.originalPresets,
          ...soundboards.reduce(
            (acc, board) => ({
              ...acc,
              [board.soundboard_id]: { ...createPresetFromCustomSoundboard(board, false), isCreatedByMe: true },
            }),
            {},
          ),
        },
        isLoadingSavedPresets: false,
      };
    }

    case ActionTypes.USER_STATE_CHANGED: {
      const { user } = action.payload;
      return { ...state, user };
    }

    case ActionTypes.SET_ACTIVE_PRESET_CATEGORY: {
      const { category } = action.payload;
      return { ...state, activePresetCategory: category };
    }

    case ActionTypes.LOAD_SAVED_STATE_FROM_SESSION_COOKIE: {
      const { state } = action.payload;
      return { ...state, playingSounds: {}, soundMetadata: {} };
    }

    case ActionTypes.DELETE_LOCAL_PRESET: {
      const { presetId } = action.payload;
      const newOriginalPresets = { ...state.originalPresets };
      delete newOriginalPresets[presetId];
      const newPresets = { ...state.presets };
      delete newPresets[presetId];
      return {
        ...state,
        presets: newPresets,
        originalPresets: newOriginalPresets,
        activePresetCategory: PresetCategory.DEFAULTS,
        activePresetId: Object.keys(newPresets)[0] ?? null,
        panel: null,
      };
    }

    case ActionTypes.DELETE_PRESET_SUCCESS: {
      const { presetId } = action.payload;
      const newOriginalPresets = { ...state.originalPresets };
      delete newOriginalPresets[presetId];
      const newPresets = { ...state.presets };
      delete newPresets[presetId];
      return {
        ...state,
        presets: newPresets,
        originalPresets: newOriginalPresets,
        activePresetCategory: PresetCategory.DEFAULTS,
        activePresetId: Object.keys(newPresets)[0] ?? null,
        panel: null,
      };
    }

    case ActionTypes.MIDI_ENABLED: {
      return {
        ...state,
        midi: {
          ...state.midi,
          enabled: true,
          error: undefined,
        },
      };
    }

    case ActionTypes.MIDI_DISABLED: {
      return {
        ...state,
        midi: {
          ...state.midi,
          enabled: false,
          error: action.payload?.error,
        },
      };
    }

    case ActionTypes.MIDI_DEVICES_DETECTED: {
      return {
        ...state,
        midi: {
          ...state.midi,
          devices: action.payload.devices,
        },
      };
    }

    case ActionTypes.MIDI_DEVICE_CONNECTED: {
      const updatedDevices = [
        ...state.midi.devices.filter(d => d.id !== action.payload.device.id),
        action.payload.device,
      ];
      return {
        ...state,
        midi: {
          ...state.midi,
          devices: updatedDevices,
        },
      };
    }

    case ActionTypes.MIDI_DEVICE_DISCONNECTED: {
      const updatedDevices = state.midi.devices.filter(d => d.id !== action.payload.device.id);
      const activeDeviceId = state.midi.activeDeviceId === action.payload.device.id ? null : state.midi.activeDeviceId;

      return {
        ...state,
        midi: {
          ...state.midi,
          devices: updatedDevices,
          activeDeviceId,
        },
      };
    }

    case ActionTypes.MIDI_SET_ACTIVE_DEVICE: {
      return {
        ...state,
        midi: {
          ...state.midi,
          activeDeviceId: action.payload.deviceId,
        },
      };
    }

    case ActionTypes.UPDATE_MIDI_MAPPING: {
      return {
        ...state,
        midi: {
          ...state.midi,
          mapping: action.payload.mapping,
        },
      };
    }

    default:
      return state;
  }
}

// Create context
const SoundboardContext = createContext<{ dispatch: React.Dispatch<Action>; state: State } | undefined>(undefined);

// Context provider component
export const SoundboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return <SoundboardContext.Provider value={{ state, dispatch }}>{children}</SoundboardContext.Provider>;
};

// Custom hook to use the soundboard context
export const useSoundboard = () => {
  const context = useContext(SoundboardContext);
  if (!context) {
    throw new Error("useSoundboard must be used within a SoundboardProvider");
  }
  return context;
};
